import { PURGE_REFUSED_REASON } from '@modules/common/pipes/can-purge.pipe';
// Common
import { injectFollowUpWording } from '@modules/common/utils/follow-up-wording';
import { Component, inject, Input } from '@angular/core';
import { ToasterService } from '@frontendlib/common/features/toaster/services/toaster.service';

// Utils
import { Like } from '@modules/common/types/like';
import { checkExhaustiveness } from '@modules/common/utils/switch';

// Types
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { Application } from '@modules/common/types/application';

// Services
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { EventsService } from '@modules/calendar-app/services/events.service';

// Components
import { BaseSidebarContextMenuComponent } from '@modules/common/components/base-sidebar-context-menu/base-sidebar-context-menu.component';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { CalendarTenantScopeService } from '../../services/calendar-tenant-scope.service';
import { mailboxTenantContext } from '@modules/common/interceptors/spaces.interceptor';
import { finalize, takeUntil } from 'rxjs/operators';
import { type RailMenuItem } from '@modules/common/components/rail-menu/rail-menu';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { clearRailFollows } from '@modules/notes/components/sidebar-context-menu/rail-clear-follows';
import { type CalendarRailKey, type CalendarRailScope, calendarRowMenu } from './calendar-rail-menu';
import { type CalendarQuickFormComponent } from '@modules/elements/components/quick-forms/calendar-quick-form/calendar-quick-form.component';

@Component({
  selector: 'app-sidebar-context-menu',
  templateUrl: './sidebar-context-menu.component.html',
  styleUrls: [
    '../../../common/components/base-sidebar-context-menu/base-sidebar-context-menu.component.less',
    './sidebar-context-menu.component.less',
  ],
  standalone: false,
})
export class SidebarContextMenuComponent extends BaseSidebarContextMenuComponent<
  StitchType.EVENT,
  StitchType.CALENDAR,
  CalendarRailKey
> {
  protected readonly followUpWording = injectFollowUpWording();
  protected readonly PURGE_REFUSED_REASON = PURGE_REFUSED_REASON;

  advancedSearchStateKey = Application.calendar;
  private readonly calendarScope = inject(CalendarTenantScopeService);
  private rowTenantChanged = false;

  /** A workspace rail row: the shared rail menu (`calendar-rail-menu.ts`). Other menus keep the template below it. */
  @Input() public rail = false;
  /** Whether the row belongs to the member's My Workspace or to the Team Workspace. */
  @Input() public railScope: CalendarRailScope = 'mine';

  /** "New event" on a rail row: a plain draft, never the row's marker or the open calendar; the form seeds the row area's calendar. */
  protected readonly railNewEvent = new CalendarEvent();
  /** "New calendar" on a rail row: a plain draft, never the row's marker. */
  protected readonly railNewCalendar = new Calendar();
  /** The row's own tenant for the rail's requests, never the selection's. */
  protected railTenantItems: ReadonlyArray<{ accountId: string | undefined }> = [];
  private readonly spaceContextStore = inject(SpaceContextStore);
  private readonly followToaster = inject(ToasterService);
  private railMenuCache: { key: string; menu: readonly RailMenuItem[] | undefined } | undefined;

  public override ngOnInit(): void {
    if (this.tenantId !== undefined && !['all_calendars', 'deleted', 'archived'].includes(this.sidebarFilterKey)) {
      this.rowTenantChanged = this.calendarScope.effectiveTenantId() !== this.tenantId;
      this.calendarScope.selectTenant(this.tenantId);
    }
    super.ngOnInit();
    this.railTenantItems = [{ accountId: this.tenantId ?? undefined }];
  }

  /** The event form's owner: the row's workspace, even if the selected area changes while the form is open. */
  protected get railDestinationTenantId(): string | undefined {
    return this.tenantId ?? undefined;
  }

  /** The rail menu of this row, or `undefined` for a row that keeps the template's own menu. */
  protected get railMenu(): readonly RailMenuItem[] | undefined {
    if (!this.rail) return undefined;
    const key = `${this.sidebarFilterKey}|${this.railScope}`;
    if (this.railMenuCache?.key !== key) {
      this.railMenuCache = { key, menu: calendarRowMenu(this.sidebarFilterKey, this.railScope) };
    }
    return this.railMenuCache.menu;
  }

  /** "View settings…" on this row: the card for the list the row opens. */
  protected openRowViewSettings(): void {
    this.openViewSettings(this.sidebarFilterKey === 'all_calendars' ? 'calendars' : 'events');
  }

  protected runRailEntry(id: string): void {
    const entry = this.railMenu?.find((candidate) => candidate?.id === id);
    // The shared renderer already answers Coming soon and refuses a disabled entry; a direct call must too.
    if (!entry || entry.comingSoon || entry.disabled) return;
    switch (id) {
      case 'settings':
        this.openRowViewSettings();
        return;
      case 'clear':
        if (this.sidebarFilterKey === 'following') void this.clearRailSubscriptions();
        else this.clearRailRow();
        return;
      case 'empty-trash-all':
        // My Workspace only (the team's entry is Coming soon): today's confirmed purge, unchanged.
        this.emptyTrash();
        return;
    }
  }

  /**
   * My Workspace · Clear my flags / follow-up reminders / snoozes: the workspace's own virtual folder (marker, not archived,
   * not trashed, not postponed past one fixed instant), events and calendars, in the row's tenant. Never `calendars_ids[]`:
   * the bulk events PUT joins the calendar only for a calendar move, so that filter would fail on the server.
   */
  private clearRailRow(): void {
    const key = this.sidebarFilterKey;
    if (this.railScope !== 'mine' || (key !== 'flagged' && key !== 'followed' && key !== 'snoozed')) return;
    const cutoff = new Date();
    const events = new EventsFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const calendars = new CalendarsFilters({ railPostponedTill: cutoff }).applyVirtualFolder(key);
    const config = { tenantItems: this.railTenantItems };
    if (key === 'flagged') {
      this.eventsService.flag(events, false, config);
      this.calendarsService.flag(calendars, false, config);
    } else if (key === 'followed') {
      this.eventsService.followUp(events, null, config);
      this.calendarsService.followUp(calendars, null, config);
    } else {
      this.eventsService.snooze(events, null, config);
      this.calendarsService.snooze(calendars, null, config);
    }
  }

  /**
   * Team Following · Clear my subscriptions. The whole-list DELETE would also clear follows of archived and
   * trashed items the view never shows, so this reads what the view lists first and unfollows those, one by one.
   */
  private async clearRailSubscriptions(): Promise<void> {
    const tenant = this.tenantId;
    if (this.railScope !== 'team' || !tenant || tenant !== this.spaceContextStore.contextId()) return;
    const events = this.eventsService;
    const calendars = this.calendarsService;
    await clearRailFollows(
      tenant,
      this.spaceContextStore,
      [
        {
          list: (offset, limit) => events.follow.api.list(tenant, offset, limit),
          unfollow: (id) => events.follow.api.unfollow(id, tenant),
          record: (id) => events.follow.record(id, tenant, false, false),
          refresh: () => events.forceRefresh(),
        },
        {
          list: (offset, limit) => calendars.follow.api.list(tenant, offset, limit),
          unfollow: (id) => calendars.follow.api.unfollow(id, tenant),
          record: (id) => calendars.follow.record(id, tenant, false, false),
          refresh: () => calendars.forceRefresh(),
        },
      ],
      this.followToaster,
    );
  }

  protected createCalendar(calendar: Calendar, form: CalendarQuickFormComponent): void {
    if (form.saveInProgress) {
      return;
    }
    form.saveInProgress = true;
    this.containerItemsService
      .create(calendar, {
        emit: true,
        toast: true,
        requestContext: this.tenantId === undefined ? undefined : mailboxTenantContext(this.tenantId),
      })
      .pipe(takeUntil(this.alive))
      .pipe(
        finalize(() => {
          form.saveInProgress = false;
        }),
      )
      .subscribe({ next: () => this.handleClose(), error: (error: unknown) => form.handleError(error) });
  }

  constructor(
    private readonly eventsService: EventsService,
    private readonly calendarsService: CalendarsService,
  ) {
    super(eventsService, calendarsService);
  }

  protected getCommonParams(): Like<CalendarEvent | Calendar> {
    let params: Like<CalendarEvent | Calendar> = {};

    switch (this.sidebarFilterKey) {
      case 'following':
      case 'shared':
      case 'assigned_to_me':
      case 'all_events':
      case 'all_calendars':
      case 'received_invites':
      case 'sent_invites':
        break;
      case 'flagged':
      case 'snoozed':
      case 'deleted':
      case 'archived':
      case 'followed':
        params = super.getCommonParams();
        break;
      default:
        checkExhaustiveness(this.sidebarFilterKey);
    }

    return params;
  }

  protected stitchItemFactory(params: Partial<CalendarEvent>): CalendarEvent {
    return new CalendarEvent({
      ...params,
      calendarId: this.rowTenantChanged ? undefined : this.selectedContainerStitchItemId,
    });
  }

  protected containerStitchItemFactory(params: Partial<Calendar>): Calendar {
    return new Calendar(params);
  }
}
